import { ArrowLeftIcon, ArrowsRightLeftIcon, SparklesIcon } from '@heroicons/react/24/outline';
import SharedButton from '../../../../global/Button/Button';
import SharedBadge from '@/global/Badge/Badge';
import {
  DetailGroup,
  getRowDetailContextLabel,
  normalizeDetailValue,
  RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX,
} from '../utils/searchResultsHelpers';

interface RelationSummary {
  leftEntity: string;
  rightEntity: string;
  showRelationArrow: boolean;
  diseaseType: string;
  score: string;
  pmids: string;
  chem: string;
  variant: string;
}

interface FullRecordDetailViewProps {
  bottomDetailRow: any;
  groupedBottomDetails: DetailGroup[];
  relationSummary: RelationSummary;
  source: any;
  tab: any;
  isSidebarOpen: boolean;
  sidebarWidth: number | string;
  isDesktop: boolean;
  searchParams: any;
  setSearchParams: (params: any) => void;
  handleDownloadRowDetail: () => void;
}

export function FullRecordDetailView({
  bottomDetailRow,
  groupedBottomDetails,
  relationSummary,
  source,
  tab,
  isSidebarOpen,
  sidebarWidth,
  isDesktop,
  searchParams,
  setSearchParams,
  handleDownloadRowDetail,
}: FullRecordDetailViewProps) {
  if (!bottomDetailRow) return null;

  const marginRight = isDesktop
    ? isSidebarOpen
      ? typeof sidebarWidth === 'number'
        ? sidebarWidth + 20
        : `calc(${sidebarWidth} + 20px)`
      : RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX + 20
    : 0;

  return (
    <div
      className="mt-4 ml-4 rounded-xl border border-slate-200 bg-white shadow-sm"
      style={{ marginRight, transition: 'margin-right 0.3s ease-out' }}
    >
      <button
        type="button"
        className="mb-3 text-sm font-medium text-slate-600 p-4 w-full text-start flex items-center shadow-sm"
        onClick={() => {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete('fullRecord');
          setSearchParams(nextParams);
        }}
      >
        <ArrowLeftIcon className="w-4 h-4 mr-2" strokeWidth={2} /> Back to results
      </button>
      <div className="mb-3 flex items-center justify-between p-4 border border-slate-200 m-4 rounded-3xl bg-gradient-to-r from-pink-50 to-white">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-pink" />
            <span className="text-xs font-normal text-gray-400 tracking-widest text-uppercase">
              {getRowDetailContextLabel(tab as string)}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-3xl font-semibold text-uppercase">{relationSummary.leftEntity}</span>
              {relationSummary.showRelationArrow && (
                <>
                  <ArrowsRightLeftIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-3xl font-normal">{relationSummary.rightEntity}</span>
                </>
              )}
            </div>
          </div>
          <div>
            <span className="text-xs font-normal text-gray-400">{relationSummary.diseaseType}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SharedBadge
            text={`Source: ${source}`}
            icon={<SparklesIcon className="w-4 h-4 mr-1" />}
            variant="primaryLight"
            className="mt-2"
          />
          <SharedButton type="button" variant="primaryOutline" size="xs" onClick={handleDownloadRowDetail}>
            Download detail
          </SharedButton>
        </div>
      </div>
      <div className="flex flex-nowrap gap-3 overflow-x-auto p-4">
        {groupedBottomDetails.map(group => (
          <section
            key={`bottom-${group.title}`}
            className="shrink-0 overflow-hidden rounded-xl border border-slate-200 pt-0 pb-3 flex flex-col"
            style={{ flex: '1 1 0', minWidth: '0' }}
          >
            <h3 className="mb-2 flex min-h-[2.25rem] items-center justify-between gap-2 text-xs font-semibold uppercase tracking-widest text-slate-600 py-2 px-3 bg-gradient-to-r from-pink-50 to-white rounded-t-xl border-b border-slate-200">
              <span className="min-w-0 truncate">• {group.title}</span>
              <span
                className="shrink-0 tabular-nums font-light text-gray-500"
                aria-label={`${group.entries.length} fields in this group`}
                style={{ fontSize: '0.7rem' }}
              >
                {group.entries.length}
              </span>
            </h3>
            <div className="space-y-1.5 px-3 pt-2">
              {group.entries.map(entry => (
                <div
                  key={`${group.title}-${entry.key}`}
                  className="flex flex-col gap-1 text-xs text-start mb-3"
                >
                  <span
                    className="overflow-hidden text-ellipsis whitespace-nowrap text-slate-500 tracking-wide"
                    style={{ fontSize: '0.65rem' }}
                    title={entry.displayLabel}
                  >
                    {entry.displayLabel}
                  </span>
                  <span
                    className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-slate-800"
                    title={normalizeDetailValue(entry.value, entry.key)}
                  >
                    {normalizeDetailValue(entry.value, entry.key)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
